import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useRegisterEdge } from '../hooks/use-register-edge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'

interface RegisterEdgeModalProps {
  open: boolean
  onClose: () => void
}

export function RegisterEdgeModal({ open, onClose }: RegisterEdgeModalProps) {
  const { t: tEdges } = useTranslation('edges')
  const [pairingCode, setPairingCode] = useState('')
  const [fingerprint, setFingerprint] = useState('')
  const [version, setVersion] = useState('')
  const [edgeId, setEdgeId] = useState<number | null>(null)
  const [edgeKey, setEdgeKey] = useState('')
  const [showKey, setShowKey] = useState(false)

  const mutation = useRegisterEdge()

  useEffect(() => {
    if (!open) {
      setPairingCode('')
      setFingerprint('')
      setVersion('')
      setEdgeId(null)
      setEdgeKey('')
      setShowKey(false)
    }
  }, [open])

  const handleRegister = () => {
    if (!pairingCode.trim()) {
      toast.error(tEdges('registerModal.pairingRequired'))
      return
    }
    mutation.mutate(
      {
        pairingCode: pairingCode.trim(),
        fingerprint: fingerprint.trim() || undefined,
        version: version.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          setEdgeId(res.edgeId)
          setEdgeKey(res.edgeKey)
          toast.success(tEdges('registerModal.registered'))
        },
      },
    )
  }

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(tEdges('registerModal.copySuccess', { label }))
    } catch {
      toast.error(tEdges('registerModal.copyFailed'))
    }
  }

  const handleClose = () => {
    onClose()
  }

  const showResult = edgeId !== null && edgeKey

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tEdges('registerModal.title')}</DialogTitle>
          <DialogDescription>
            {tEdges('registerModal.description')}
          </DialogDescription>
        </DialogHeader>

        {!showResult ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="pairing">
                {tEdges('registerModal.pairingCodeLabel')}
              </Label>
              <Input
                id="pairing"
                placeholder={tEdges('registerModal.pairingCodePlaceholder')}
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fingerprint">
                {tEdges('registerModal.fingerprintLabel')}
              </Label>
              <Input
                id="fingerprint"
                placeholder={tEdges('registerModal.fingerprintPlaceholder')}
                value={fingerprint}
                onChange={(e) => setFingerprint(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">
                {tEdges('registerModal.edgeVersionLabel')}
              </Label>
              <Input
                id="version"
                placeholder={tEdges('registerModal.edgeVersionPlaceholder')}
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {tEdges('registerModal.edgeIdLabel')}
              </p>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted">{edgeId}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleCopy(
                      String(edgeId),
                      tEdges('registerModal.edgeIdShort'),
                    )
                  }
                >
                  {tEdges('registerModal.copy')}
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {tEdges('registerModal.edgeKeyLabel')}
              </p>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted">
                  {showKey ? edgeKey : '••••••••••••••••'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowKey((v) => !v)}
                >
                  {showKey
                    ? tEdges('registerModal.hide')
                    : tEdges('registerModal.show')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleCopy(edgeKey, tEdges('registerModal.edgeKeyShort'))
                  }
                >
                  {tEdges('registerModal.copy')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {tEdges('registerModal.configureWith')}
                <br />
                <code>
                  {tEdges('registerModal.edgeIdConfig')} {edgeId}
                </code>
                <br />
                <code>
                  {tEdges('registerModal.edgeKeyConfig')} {edgeKey}
                </code>
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!showResult ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                {tEdges('registerModal.cancel')}
              </Button>
              <Button onClick={handleRegister} disabled={mutation.isPending}>
                {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
                {tEdges('registerModal.register')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>
              {tEdges('registerModal.done')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
